/**
 * `useAgentDetailCommandsFromAgent` — open Team-of-Agents detail
 * dialogs from a concierge `lx_*` tool.
 *
 * Concierge slash-commands Part A §2.4 (handoff
 * `concierge-slash-commands-lexide-tools-handoff-2026-06-30.md`).
 * Distinct from the shipped `useTeamPaneCommandsFromAgent` (which
 * handles the team-pane open + focus-row verbs); this hook is the
 * "open a DETAIL dialog for one mission / one harness task" verb.
 *
 * Wires:
 *   • `lx_show_mission_detail {mission_id}` → useUIStore.openMissionDetail
 *   • `lx_show_harness_detail {task_id}`    → useUIStore.openHarnessDetail
 *
 * Both setters live on useUIStore (NOT on a fictional `useMissionsStore.
 * openDetail` / `useHarnessStore.openDetail` as the catalog §4.5 named —
 * see parent plan §14's corrected store map).
 *
 * Mount once at the App level (the detail dialogs are global chrome —
 * they render over any route).
 *
 * Contract: act on `phase === "start"` only; accept legacy
 * `data.name`/`data.args` shape. Pure handler extracted for unit tests.
 */

import { useEventStream } from "./useEventStream";
import { useUIStore } from "@/stores/useUIStore";

export interface AgentEventPayloadRuntime {
  data?: {
    phase?: string;
    toolName?: string;
    name?: string;
    toolInput?: Record<string, unknown>;
    args?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

export interface AgentDetailCommandDeps {
  openMissionDetail: (missionId: string) => void;
  openHarnessDetail: (taskId: string) => void;
}

export function handleAgentDetailCommand(
  payload: unknown,
  deps: AgentDetailCommandDeps,
): void {
  const typed = payload as AgentEventPayloadRuntime;
  if (typed.data?.phase !== "start") return;
  const tool = typed.data?.toolName ?? typed.data?.name;
  const input = typed.data?.toolInput ?? typed.data?.args;

  switch (tool) {
    case "lx_show_mission_detail": {
      const id = input?.mission_id;
      if (typeof id !== "string" || id.length === 0) return;
      deps.openMissionDetail(id);
      return;
    }
    case "lx_show_harness_detail": {
      const id = input?.task_id;
      if (typeof id !== "string" || id.length === 0) return;
      deps.openHarnessDetail(id);
      return;
    }
    default:
      return;
  }
}

export function useAgentDetailCommandsFromAgent(): void {
  const openMissionDetail = useUIStore((s) => s.openMissionDetail);
  const openHarnessDetail = useUIStore((s) => s.openHarnessDetail);

  useEventStream("session.tool", (payload) => {
    handleAgentDetailCommand(payload, { openMissionDetail, openHarnessDetail });
  });
}
